/**
 * (c) 2023, Center for Computational Thinking and Design at Aarhus University and contributors
 * Modifications (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { IconType } from "react-icons/lib";
import {
  RiAddLine,
  RiFolderOpenLine,
  RiInformationLine,
  RiUpload2Line,
} from "react-icons/ri";
import { FormattedMessage, useIntl } from "react-intl";
import { Link as RouterLink, useNavigate } from "react-router";
import { CarouselRow } from "@microbit/ui-carousel";
import {
  ProjectCard,
  sortProjects,
  useProjectActions,
} from "@microbit/ui-patterns";
import DefaultPageLayout, {
  HomeToolbarItem,
} from "../components/DefaultPageLayout";
import { createHelpCards } from "../components/HelpCards";
import HomepageBanner from "../components/HomepageBanner";
import HomepageFooter from "../components/HomepageFooter";
import { createLessonCards } from "../components/LessonCards";
import LoadProjectInput, {
  LoadProjectInputRef,
} from "../components/LoadProjectInput";
import { NameProjectDialog } from "../components/NameProjectDialog";
import ProjectIcon from "../components/ProjectIcon";
import { createProjectIdeaCards } from "../components/ProjectIdeaCards";
import { useLogging } from "../logging/logging-hooks";
import { isNativePlatform } from "../platform";
import {
  Button,
  Card,
  CardBody,
  css,
  Icon,
  IconButton,
  darkSurface,
  LinkBox,
  LinkOverlayButton,
  Text,
  TooltipButton,
  useBreakpointValue,
  VStack,
} from "@microbit/ui";
import {
  loadProjectAndModelFromStorage,
  useSettings,
  useStore,
} from "../store";
import { createDataSamplesPageUrl, createProjectsPageUrl } from "../urls";
import { useDefaultProjectName } from "../hooks/project-hooks";

const carouselRowClassName = css({ _shortHeight: { py: 4 } });

const HomePage = () => {
  const intl = useIntl();
  const [{ languageId }] = useSettings();
  const resetWelcomeDialog = useStore((s) => s.resetWelcomeDialog);
  useEffect(() => {
    resetWelcomeDialog();
  }, [resetWelcomeDialog]);

  return (
    <DefaultPageLayout toolbarItemsRight={<HomeToolbarItem />}>
      <HomepageBanner />
      <ProjectRow />
      <CarouselRow
        carouselItems={createProjectIdeaCards(intl, languageId)}
        title={<FormattedMessage id="project-ideas-row-title" />}
        navigation={!isNativePlatform()}
        className={carouselRowClassName}
      />
      <CarouselRow
        carouselItems={createLessonCards(intl)}
        title={<FormattedMessage id="teacher-resources-row-title" />}
        navigation={!isNativePlatform()}
        className={carouselRowClassName}
      />
      <CarouselRow
        carouselItems={createHelpCards(intl)}
        title={<FormattedMessage id="help-resources-row-title" />}
        navigation={!isNativePlatform()}
        className={carouselRowClassName}
      />
      <HomepageFooter />
    </DefaultPageLayout>
  );
};

const ProjectRow = () => {
  const numCardsDisplayed = 10;
  const tooltipPlacement = useBreakpointValue<"bottom" | "right">({
    base: "bottom",
    sm: "right",
  });
  const navigate = useNavigate();
  const allProjectData = useStore((s) => s.allProjectData);
  const renameProject = useStore((s) => s.setProjectName);
  const duplicateProject = useStore((s) => s.duplicateProject);
  const deleteProjects = useStore((s) => s.deleteProjects);
  const logging = useLogging();

  const handleOpenProject = useCallback(
    async (id: string) => {
      logging.event({
        type: "project_open",
        detail: { surface: "home" },
      });
      await loadProjectAndModelFromStorage(id);
      void navigate(createDataSamplesPageUrl());
    },
    [logging, navigate]
  );

  const actions = useProjectActions({
    projects: allProjectData,
    onRename: async (id, name) => {
      logging.event({ type: "project_rename", detail: { surface: "home" } });
      await renameProject(name, id);
    },
    onDuplicate: async (id, name) => {
      logging.event({ type: "project_duplicate", detail: { surface: "home" } });
      await duplicateProject(id, name);
    },
    onDelete: async (ids) => {
      logging.event({
        type: "project_delete",
        detail: { surface: "home", count: ids.length },
      });
      await deleteProjects(ids);
    },
  });

  return (
    <>
      {actions.dialogs}
      <CarouselRow
        actions={[
          <ImportProjectButton key="importProject" />,
          <ViewAllProjectsLink key="viewAll" />,
        ]}
        carouselItems={
          [
            <NewProjectCard key="new-project" />,
            ...sortProjects(allProjectData, "timestamp", "desc")
              .map((projectData) => (
                <ProjectCard
                  key={projectData.id}
                  project={projectData}
                  description={projectData.actions
                    .map((a) => a.name)
                    .join(", ")}
                  bodyCss={{ _shortHeight: { p: 3 } }}
                  onDelete={actions.delete}
                  onRename={actions.rename}
                  onDuplicate={actions.duplicate}
                  onOpen={handleOpenProject}
                >
                  <ProjectIcon short />
                </ProjectCard>
              ))
              .slice(0, numCardsDisplayed),
            allProjectData.length > numCardsDisplayed ? (
              <ViewAllProjectsCard key="view-all" />
            ) : undefined,
          ].filter(Boolean) as JSX.Element[]
        }
        navigation={!isNativePlatform()}
        className={carouselRowClassName}
        title={<FormattedMessage id="my-projects-row-title" />}
        titleSuffix={
          !isNativePlatform() && (
            <TooltipButton
              hasArrow
              placement={tooltipPlacement}
              label={
                <VStack textAlign="left" alignItems="flex-start" m={3}>
                  <Text>
                    <FormattedMessage id="project-storage-tooltip" />
                  </Text>
                </VStack>
              }
            >
              <Icon
                as={RiInformationLine}
                css={{ opacity: 0.7, width: 5, height: 5 }}
              />
            </TooltipButton>
          )
        }
      />
    </>
  );
};

const ViewAllProjectsLink = () => {
  return (
    <RouterLink
      to={createProjectsPageUrl()}
      className={css({
        color: "brand.700",
        fontWeight: "semibold",
        borderRadius: "md",
        px: 2,
        py: 1,
        textDecoration: "none",
        _hover: { textDecoration: "underline" },
        _focusVisible: { focusRing: "outline" },
      })}
    >
      <FormattedMessage id="view-all-projects" />
    </RouterLink>
  );
};

interface ActionCardProps {
  onClick: () => void;
  icon: IconType;
  textId: string;
}

const ActionCard = ({ onClick, icon, textId }: ActionCardProps) => {
  return (
    <LinkBox h="100%" display="flex">
      <Card
        css={{
          flexGrow: 1,
          overflow: "hidden",
          minH: "233px",
          _shortHeight: { minH: "160px" },
        }}
      >
        <CardBody
          {...darkSurface}
          css={{
            display: "flex",
            backgroundColor: "brand.500",
            color: "white",
            _shortHeight: { p: 3 },
          }}
        >
          <VStack h="100%" w="100%" gap={0} justifyContent="space-evenly">
            <VStack>
              <Icon
                as={icon}
                css={{
                  width: 20,
                  height: 20,
                  _shortHeight: { width: 10, height: 10 },
                }}
              />
            </VStack>
            <LinkOverlayButton
              onClick={onClick}
              css={{
                h: 8,
                fontSize: "xl",
              }}
            >
              <FormattedMessage id={textId} />
            </LinkOverlayButton>
          </VStack>
        </CardBody>
      </Card>
    </LinkBox>
  );
};

const NewProjectCard = () => {
  const newSession = useStore((s) => s.newSession);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const logging = useLogging();

  const handleNameProjectSave = useCallback(
    async (projectName: string) => {
      setIsOpen(false);
      logging.event({
        type: "project_create",
        detail: { surface: "home" },
      });
      await newSession(projectName);
      void navigate(createDataSamplesPageUrl());
    },
    [logging, newSession, navigate]
  );

  const handleOpenNameDialog = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleCloseNameDialog = useCallback(() => {
    setIsOpen(false);
  }, []);

  const projectName = useDefaultProjectName();

  return (
    <>
      <NameProjectDialog
        projectName={projectName}
        isOpen={isOpen}
        onClose={handleCloseNameDialog}
        onSave={handleNameProjectSave}
        helperText={null}
        heading={<FormattedMessage id="create-project-dialog-heading" />}
        confirmText={<FormattedMessage id="create-project" />}
      />
      <ActionCard
        onClick={handleOpenNameDialog}
        icon={RiAddLine}
        textId="newpage-new-project-title"
      />
    </>
  );
};

const ImportProjectButton = () => {
  const intl = useIntl();
  const loadProjectRef = useRef<LoadProjectInputRef>(null);
  const handleContinueSessionFromFile = useCallback(() => {
    loadProjectRef.current?.chooseFile("replaceProject");
  }, []);
  return (
    <>
      <LoadProjectInput ref={loadProjectRef} accept=".json,.hex" />
      <IconButton
        variant="ghost"
        aria-label={intl.formatMessage({ id: "import-file-action" })}
        onPress={handleContinueSessionFromFile}
        css={{ display: { base: "inline-flex", sm: "none" } }}
      >
        <Icon as={RiUpload2Line} />
      </IconButton>
      <Button
        startIcon={<Icon as={RiUpload2Line} />}
        onPress={handleContinueSessionFromFile}
        css={{ display: { base: "none", sm: "inline-flex" } }}
      >
        <FormattedMessage id="import-file-action" />
      </Button>
    </>
  );
};

const ViewAllProjectsCard = () => {
  const navigate = useNavigate();
  const handleClick = useCallback(() => {
    void navigate(createProjectsPageUrl());
  }, [navigate]);
  return (
    <ActionCard
      onClick={handleClick}
      icon={RiFolderOpenLine}
      textId="view-all-projects"
    />
  );
};

export default HomePage;
